// Extensões do editor de notas (spec §4.2): tarefas, marca-texto, imagem e tabela dentro da nota. Também valem para o
// schema da face dos cards na grade (Grid.tsx), senão uma nota com tarefa cai no texto plano.
import { type AnyExtension, Extension, Mark, markInputRule, mergeAttributes, Node } from "@tiptap/core";
// ponytail: dependência transitiva do starter-kit (mesma versão); declarar no package.json quando a dona dele mexer.
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { addRowAfter, goToNextCell, isInTable, tableEditing } from "@tiptap/pm/tables";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";

/** ==texto== ou Ctrl/Cmd+Shift+H. */
const Highlight = Mark.create({
  name: "highlight",
  parseHTML: () => [{ tag: "mark" }],
  renderHTML: ({ HTMLAttributes }) => ["mark", HTMLAttributes, 0],
  addInputRules() {
    return [markInputRule({ find: /(?:^|\s)(==(?!\s)([^=]+)(?<!\s)==)$/, type: this.type })];
  },
  addKeyboardShortcuts() {
    return { "Mod-Shift-h": () => this.editor.commands.toggleMark(this.name) };
  },
});

/** Só arquivos da própria conta (/media/<blobId>): colar HTML de fora nunca carrega imagem de terceiros. */
export const MEDIA_SRC = /^\/media\/[\w.-]+(\?w=\d+)?$/;
const NoteImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes: () => ({ src: { default: null }, alt: { default: null } }),
  parseHTML: () => [{ tag: "img[src]", getAttrs: (el) => (MEDIA_SRC.test((el as HTMLElement).getAttribute("src") ?? "") ? null : false) }],
  renderHTML: ({ HTMLAttributes: a }) => ["img", mergeAttributes(a, { src: MEDIA_SRC.test(String(a.src ?? "")) ? a.src : null, alt: a.alt ?? "", loading: "lazy" })],
});

// Tabela: nós do prosemirror-tables (o @tiptap/pm já traz) com o papel de cada um no schema.
const ROLE: Record<string, string> = { table: "table", tableRow: "row", tableCell: "cell", tableHeader: "header_cell" };
const span = (attr: string) => ({ default: 1, parseHTML: (el: HTMLElement) => Number(el.getAttribute(attr)) || 1 });
const cell = (name: string, tag: "td" | "th") =>
  Node.create({
    name,
    content: "block+",
    isolating: true,
    addAttributes: () => ({ colspan: span("colspan"), rowspan: span("rowspan"), colwidth: { default: null, rendered: false } }),
    parseHTML: () => [{ tag }],
    renderHTML: ({ HTMLAttributes }) => [tag, HTMLAttributes, 0],
  });
const TableRow = Node.create({ name: "tableRow", content: "(tableCell | tableHeader)*", parseHTML: () => [{ tag: "tr" }], renderHTML: ({ HTMLAttributes }) => ["tr", HTMLAttributes, 0] });
const Table = Node.create({
  name: "table",
  group: "block",
  content: "tableRow+",
  isolating: true,
  extendNodeSchema: (ext) => (ROLE[ext.name] ? { tableRole: ROLE[ext.name] } : {}),
  parseHTML: () => [{ tag: "table" }],
  renderHTML: ({ HTMLAttributes }) => ["div", { class: "table-wrap" }, ["table", HTMLAttributes, ["tbody", 0]]],
  addProseMirrorPlugins: () => [tableEditing()],
  addKeyboardShortcuts() {
    // Tab anda entre as células; na última sai do editor (sem prender o teclado). Linha nova: comando "/linha".
    const move = (dir: 1 | -1) => () => {
      const { state, view } = this.editor;
      return isInTable(state) && goToNextCell(dir)(state, view.dispatch);
    };
    return { Tab: move(1), "Shift-Tab": move(-1) };
  },
});
export const addTableRow = (s: Parameters<typeof addRowAfter>[0], d: Parameters<typeof addRowAfter>[1]) => isInTable(s) && addRowAfter(s, d);
export const tableDoc = (cols = 3, rows = 3) => ({
  type: "table",
  content: Array.from({ length: rows }, (_, r) => ({
    type: "tableRow",
    content: Array.from({ length: cols }, () => ({ type: r ? "tableCell" : "tableHeader", content: [{ type: "paragraph" }] })),
  })),
});

export const WIKI = /\[\[([^[\]\n]{1,200})\]\]/g;

/** [[Título]] ganha a classe "wiki" e o clique abre a nota citada (`open` devolve false se não achou). */
export const WikiLinks = Extension.create<{ open: (title: string) => boolean }>({
  name: "wikiLinks",
  addOptions: () => ({ open: () => false }),
  addProseMirrorPlugins() {
    const open = (title: string) => this.options.open(title);
    return [
      new Plugin({
        key: new PluginKey("wikiLinks"),
        props: {
          decorations(state) {
            const decos: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text?.includes("[[")) return;
              for (const m of node.text.matchAll(WIKI)) {
                const from = pos + m.index!;
                decos.push(Decoration.inline(from, from + m[0].length, { class: "wiki", "data-wiki": m[1], title: `Abrir “${m[1]}”` }));
              }
            });
            return DecorationSet.create(state.doc, decos);
          },
          handleClick: (_view, _pos, e) => {
            const el = (e.target as HTMLElement).closest<HTMLElement>("[data-wiki]");
            return !!el && !e.shiftKey && open(el.dataset.wiki!);
          },
        },
      }),
    ];
  },
});

const a11y = { checkboxLabel: (node: { textContent: string }, checked: boolean) => `${checked ? "Feita" : "Por fazer"}: ${node.textContent || "tarefa vazia"}` };

/** Schema das notas: o mesmo no editor, na grade e no modo escrita. */
export const noteExtensions: AnyExtension[] = [StarterKit, TaskList, TaskItem.configure({ nested: true, a11y }), Highlight, NoteImage, Table, TableRow, cell("tableHeader", "th"), cell("tableCell", "td")];
